import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import api, { fmtINR } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const emptyAddr = {
  full_name: "", mobile: "", email: "", line1: "", line2: "",
  city: "", state: "Rajasthan", pincode: "", gst_number: "",
};

const Input = ({ label, value, onChange, ...rest }) => (
  <div>
    <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#FF6D00] outline-none" {...rest} />
  </div>
);

export default function Checkout() {
  const { items, totals, gst, total, clear } = useCart();
  const [billing, setBilling] = useState(emptyAddr);
  const [shipSame, setShipSame] = useState(true);
  const [shipping, setShipping] = useState(emptyAddr);
  const [payment, setPayment] = useState("razorpay");
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rzEnabled, setRzEnabled] = useState(false);
  const [rzKey, setRzKey] = useState("");
  const placingRef = useRef(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/settings").then((r) => { setRzEnabled(r.data.razorpay_enabled); setRzKey(r.data.razorpay_key_id); });
  }, []);

  useEffect(() => {
    if (items.length === 0 && !placingRef.current) nav("/products");
  }, [items.length, nav]);

  const finalTotal = total - discount;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const r = await api.post(`/coupons/validate?code=${encodeURIComponent(coupon.trim())}&order_amount=${totals.subtotal}`);
      setDiscount(r.data.discount);
      toast.success(`Coupon applied: -${fmtINR(r.data.discount)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid coupon");
    }
  };

  const validate = () => {
    for (const k of ["full_name", "mobile", "email", "line1", "city", "state", "pincode"]) {
      if (!billing[k]?.trim()) { toast.error(`Billing ${k.replace("_", " ")} required`); return false; }
    }
    if (!/^\d{10}$/.test(billing.mobile)) { toast.error("Enter a valid 10-digit mobile"); return false; }
    if (!/^\d{6}$/.test(billing.pincode)) { toast.error("Enter a valid 6-digit pincode"); return false; }
    return true;
  };

  const loadRazorpay = () => new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });

  const placeOrder = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((it) => ({
          product_id: it.product_id, quantity: it.quantity,
          subscription: it.subscription, installation: it.installation,
          vehicle_type: it.vehicle_type,
        })),
        billing,
        shipping: shipSame ? null : shipping,
        payment_method: payment,
        coupon_code: coupon.trim() ? coupon.trim() : null,
      };
      const r = await api.post("/orders", payload);
      const order = r.data;

      if (payment === "razorpay") {
        if (!rzEnabled) {
          toast.error("Online payment not configured. Choose COD or Quote.");
          setLoading(false);
          return;
        }
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Payment SDK failed to load"); setLoading(false); return; }
        const rz = new window.Razorpay({
          key: rzKey,
          amount: Math.round(order.total * 100),
          currency: "INR",
          order_id: order.razorpay_order_id,
          name: "KSPB Enterprises LLP",
          description: `Order ${order.order_number}`,
          prefill: { name: billing.full_name, email: billing.email, contact: billing.mobile },
          theme: { color: "#FF6D00" },
          handler: async (resp) => {
            await api.post("/orders/verify-payment", {
              order_id: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            placingRef.current = true;
            nav(`/order-success/${order.order_number}?mobile=${billing.mobile}`);
            clear();
          },
          modal: { ondismiss: () => setLoading(false) },
        });
        rz.open();
      } else {
        placingRef.current = true;
        nav(`/order-success/${order.order_number}?mobile=${billing.mobile}`);
        clear();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="checkout-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">Checkout</h1>
      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="p-6 rounded-2xl border border-slate-200 bg-white">
            <h2 className="font-heading text-lg font-semibold text-[#0A192F] mb-4">Billing Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Full Name*" value={billing.full_name} onChange={(v) => setBilling({ ...billing, full_name: v })} data-testid="bill-name" />
              <Input label="Mobile*" value={billing.mobile} onChange={(v) => setBilling({ ...billing, mobile: v })} inputMode="numeric" maxLength={10} data-testid="bill-mobile" />
              <Input label="Email*" type="email" value={billing.email} onChange={(v) => setBilling({ ...billing, email: v })} data-testid="bill-email" />
              <Input label="Pincode*" value={billing.pincode} onChange={(v) => setBilling({ ...billing, pincode: v })} inputMode="numeric" maxLength={6} data-testid="bill-pincode" />
              <div className="sm:col-span-2"><Input label="Address Line 1*" value={billing.line1} onChange={(v) => setBilling({ ...billing, line1: v })} data-testid="bill-line1" /></div>
              <div className="sm:col-span-2"><Input label="Address Line 2" value={billing.line2} onChange={(v) => setBilling({ ...billing, line2: v })} /></div>
              <Input label="City*" value={billing.city} onChange={(v) => setBilling({ ...billing, city: v })} data-testid="bill-city" />
              <Input label="State*" value={billing.state} onChange={(v) => setBilling({ ...billing, state: v })} data-testid="bill-state" />
              <Input label="GSTIN (optional)" value={billing.gst_number} onChange={(v) => setBilling({ ...billing, gst_number: v })} />
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={shipSame} onChange={(e) => setShipSame(e.target.checked)} className="w-4 h-4 accent-[#FF6D00]" data-testid="ship-same" />
              Shipping address is same as billing
            </label>
          </div>

          {!shipSame && (
            <div className="p-6 rounded-2xl border border-slate-200 bg-white">
              <h2 className="font-heading text-lg font-semibold text-[#0A192F] mb-4">Shipping Address</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Full Name" value={shipping.full_name} onChange={(v) => setShipping({ ...shipping, full_name: v })} />
                <Input label="Mobile" value={shipping.mobile} onChange={(v) => setShipping({ ...shipping, mobile: v })} />
                <Input label="Email" type="email" value={shipping.email} onChange={(v) => setShipping({ ...shipping, email: v })} />
                <Input label="Pincode" value={shipping.pincode} onChange={(v) => setShipping({ ...shipping, pincode: v })} />
                <div className="sm:col-span-2"><Input label="Address" value={shipping.line1} onChange={(v) => setShipping({ ...shipping, line1: v })} /></div>
                <Input label="City" value={shipping.city} onChange={(v) => setShipping({ ...shipping, city: v })} />
                <Input label="State" value={shipping.state} onChange={(v) => setShipping({ ...shipping, state: v })} />
              </div>
            </div>
          )}

          <div className="p-6 rounded-2xl border border-slate-200 bg-white">
            <h2 className="font-heading text-lg font-semibold text-[#0A192F] mb-4">Payment Method</h2>
            <div className="space-y-3">
              {[
                { key: "razorpay", label: `Online — UPI / Card / Netbanking (Razorpay)${rzEnabled ? "" : " · configure keys in .env"}`, disabled: !rzEnabled },
                { key: "cod", label: "Cash on Delivery (available for select pincodes)", disabled: false },
                { key: "quote", label: "Request final quote before payment", disabled: false },
              ].map((opt) => (
                <label key={opt.key} className={`flex items-center gap-3 p-3 rounded-lg border ${payment === opt.key ? "border-[#FF6D00] bg-orange-50" : "border-slate-200"} ${opt.disabled ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}>
                  <input type="radio" checked={payment === opt.key} onChange={() => setPayment(opt.key)} disabled={opt.disabled} className="accent-[#FF6D00]" data-testid={`pay-${opt.key}`} />
                  <span className="text-sm text-slate-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <aside className="p-6 rounded-2xl bg-slate-50 border border-slate-200 h-fit sticky top-24">
          <h2 className="font-heading text-lg font-semibold text-[#0A192F]">Order summary</h2>
          <div className="mt-4 max-h-56 overflow-auto space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm text-slate-600">
                <span className="truncate mr-3">{it.name} × {it.quantity}</span>
                <span>{fmtINR(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200 text-sm space-y-1">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmtINR(totals.subtotal)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Installation</span><span>{fmtINR(totals.installation)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Subscription</span><span>{fmtINR(totals.subscription)}</span></div>
            <div className="flex justify-between text-slate-600"><span>GST (18%)</span><span>{fmtINR(gst)}</span></div>
            {discount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>-{fmtINR(discount)}</span></div>}
          </div>
          <div className="mt-3 flex gap-2">
            <input placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)} className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" data-testid="coupon-input" />
            <button onClick={applyCoupon} className="px-4 h-10 rounded-lg border border-[#0A192F] text-[#0A192F] text-sm font-medium" data-testid="apply-coupon">Apply</button>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between font-heading font-bold text-lg text-[#0A192F]">
            <span>Total payable</span><span>{fmtINR(finalTotal)}</span>
          </div>
          <button onClick={placeOrder} disabled={loading} data-testid="place-order-btn" className="mt-5 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#FF6D00] text-white font-semibold hover:bg-[#ff8433] disabled:opacity-60 transition-colors">
            {loading ? "Processing…" : "Place Order"}
          </button>
          <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 justify-center">
            <ShieldCheck className="w-4 h-4 text-[#00E676]" /> Secure & encrypted checkout
          </div>
        </aside>
      </div>
    </div>
  );
}
